import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import {
    useCircuitBreakerState,
    useCallCircuitBreaker,
    useResetCircuitBreaker,
} from '@/api/hooks/useCircuitBreakerTest';
import type { CircuitBreakerCallResponse, CircuitBreakerSnapshot } from '@/api/types';

function StateIndicator({ state }: { state: CircuitBreakerSnapshot['state'] }) {
    const colors = {
        closed: 'bg-green-500',
        open: 'bg-red-500',
        half_open: 'bg-yellow-500',
    };

    const labels = {
        closed: 'Closed',
        open: 'Open',
        half_open: 'Half Open',
    };

    return (
        <div className="flex items-center gap-2">
            <span className={`inline-block h-3 w-3 rounded-full ${colors[state]}`} />
            <span className="font-medium">{labels[state]}</span>
        </div>
    );
}

function CircuitStateCard({ snapshot, serviceName }: { snapshot: CircuitBreakerSnapshot; serviceName?: string }) {
    return (
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-dark">
            {serviceName && (
                <h3 className="mb-3 font-mono text-sm text-gray-500 dark:text-gray-400">
                    {serviceName}
                </h3>
            )}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">State</span>
                    <StateIndicator state={snapshot.state} />
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Failure Count</span>
                    <span className={`font-mono ${snapshot.failure_count > 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                        {snapshot.failure_count}
                    </span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Last Failure</span>
                    <span className="font-mono text-sm">
                        {snapshot.last_failure_at
                            ? new Date(snapshot.last_failure_at).toLocaleTimeString()
                            : '-'}
                    </span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Opened At</span>
                    <span className="font-mono text-sm">
                        {snapshot.opened_at
                            ? new Date(snapshot.opened_at).toLocaleTimeString()
                            : '-'}
                    </span>
                </div>
            </div>
        </div>
    );
}

function CallResultCard({ result }: { result: CircuitBreakerCallResponse }) {
    return (
        <div
            className={`rounded-lg border p-4 ${
                result.success
                    ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                    : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
            }`}
        >
            <div className="mb-2 flex items-center justify-between">
                <span
                    className={`font-semibold ${
                        result.success
                            ? 'text-green-700 dark:text-green-400'
                            : 'text-red-700 dark:text-red-400'
                    }`}
                >
                    {result.success ? 'Success' : 'Failed'}
                </span>
                <span className="font-mono text-sm text-gray-500 dark:text-gray-400">
                    {result.duration_ms}ms
                </span>
            </div>
            {result.error && (
                <div className="mb-2">
                    <span className="text-sm text-red-600 dark:text-red-400">
                        {result.error}: {result.message}
                    </span>
                </div>
            )}
            {result.data && (
                <details className="mt-2">
                    <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-400">
                        Response data
                    </summary>
                    <pre className="mt-2 max-h-40 overflow-auto rounded-sm bg-gray-100 p-2 text-xs dark:bg-gray-800">
                        {JSON.stringify(result.data, null, 2)}
                    </pre>
                </details>
            )}
        </div>
    );
}

export function CircuitBreakerTestPage() {
    const [simulateFailure, setSimulateFailure] = useState(false);
    const [delayMs, setDelayMs] = useState('0');
    const [callHistory, setCallHistory] = useState<CircuitBreakerCallResponse[]>([]);

    const { data: stateData, isLoading: isLoadingState } = useCircuitBreakerState();
    const callMutation = useCallCircuitBreaker();
    const resetMutation = useResetCircuitBreaker();

    const handleCall = async () => {
        const result = await callMutation.mutateAsync({
            simulateFailure,
            delayMs: parseInt(delayMs, 10) || 0,
        });
        setCallHistory((prev) => [result, ...prev].slice(0, 20));
    };

    const handleReset = async () => {
        await resetMutation.mutateAsync();
        setCallHistory([]);
    };

    const handleBurstCalls = async (count: number, withFailure: boolean) => {
        for (let i = 0; i < count; i++) {
            const result = await callMutation.mutateAsync({
                simulateFailure: withFailure,
                delayMs: 0,
            });
            setCallHistory((prev) => [result, ...prev].slice(0, 20));
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Circuit Breaker Test
                </h2>
                <p className="mt-1 text-gray-600 dark:text-gray-400">
                    Test the circuit breaker behavior with simulated API calls
                </p>
            </div>

                <div className="grid gap-6 lg:grid-cols-2">
                    {/* Controls */}
                    <div className="space-y-6">
                        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-dark">
                            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                                Test Controls
                            </h2>

                            <div className="space-y-4">
                                <Checkbox
                                    label="Simulate failure"
                                    checked={simulateFailure}
                                    onChange={(e) => setSimulateFailure(e.target.checked)}
                                    helperText="When checked, the API call will throw an error"
                                />

                                <Input
                                    label="Delay (ms)"
                                    type="number"
                                    min="0"
                                    max="5000"
                                    value={delayMs}
                                    onChange={(e) => setDelayMs(e.target.value)}
                                    helperText="Artificial delay to simulate slow responses"
                                />

                                <div className="flex flex-wrap gap-2 pt-2">
                                    <Button
                                        onClick={handleCall}
                                        isLoading={callMutation.isPending}
                                        disabled={callMutation.isPending}
                                    >
                                        Make Call
                                    </Button>
                                    <Button
                                        variant="danger"
                                        onClick={handleReset}
                                        isLoading={resetMutation.isPending}
                                        disabled={resetMutation.isPending}
                                    >
                                        Reset Circuit
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-dark">
                            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                                Quick Actions
                            </h2>
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => handleBurstCalls(3, false)}
                                    disabled={callMutation.isPending}
                                >
                                    3x Success
                                </Button>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleBurstCalls(3, true)}
                                    disabled={callMutation.isPending}
                                >
                                    3x Failure (Trip)
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handleBurstCalls(5, true)}
                                    disabled={callMutation.isPending}
                                >
                                    5x Failure
                                </Button>
                            </div>
                            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                                Default threshold is 3 failures. After 3 failures, circuit opens for 60 seconds.
                            </p>
                        </div>
                    </div>

                    {/* Current State */}
                    <div className="space-y-6">
                        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-dark">
                            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                                Current Circuit State
                            </h2>
                            {isLoadingState ? (
                                <div className="text-gray-500 dark:text-gray-400">Loading...</div>
                            ) : stateData?.circuit_state ? (
                                <CircuitStateCard
                                    snapshot={stateData.circuit_state}
                                    serviceName={stateData.service_name}
                                />
                            ) : (
                                <div className="text-gray-500 dark:text-gray-400">
                                    No state available
                                </div>
                            )}
                            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                                Auto-refreshes every 5 seconds
                            </p>
                        </div>

                        {/* State Legend */}
                        <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-dark">
                            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                                State Legend
                            </h2>
                            <div className="space-y-3 text-sm">
                                <div className="flex items-start gap-3">
                                    <span className="mt-1 inline-block h-3 w-3 shrink-0 rounded-full bg-green-500" />
                                    <div>
                                        <span className="font-medium">Closed</span>
                                        <p className="text-gray-500 dark:text-gray-400">
                                            Normal operation. Requests pass through.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="mt-1 inline-block h-3 w-3 shrink-0 rounded-full bg-red-500" />
                                    <div>
                                        <span className="font-medium">Open</span>
                                        <p className="text-gray-500 dark:text-gray-400">
                                            Failure threshold reached. Requests are rejected immediately.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="mt-1 inline-block h-3 w-3 shrink-0 rounded-full bg-yellow-500" />
                                    <div>
                                        <span className="font-medium">Half Open</span>
                                        <p className="text-gray-500 dark:text-gray-400">
                                            Recovery timeout passed. Next request is a test.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Call History */}
                <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-dark">
                    <div className="mb-4 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Call History
                        </h2>
                        {callHistory.length > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setCallHistory([])}
                            >
                                Clear
                            </Button>
                        )}
                    </div>
                    {callHistory.length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400">
                            No calls made yet. Use the controls above to test the circuit breaker.
                        </p>
                    ) : (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {callHistory.map((result, index) => (
                                <CallResultCard key={index} result={result} />
                            ))}
                        </div>
                    )}
                </div>
        </div>
    );
}
